import Card from "app/components/Card";
import TicketTypeIcon from "app/components/TicketTypeIcon";
import clsx from "clsx";
import React, { useState } from "react";
import { usePlanningSprintDrop } from "../hooks/usePlanningSprintDrop";
import { IPlanningViewsGroup, PlanningViewBy } from "../types";
import { dateFormatter } from "utils/helpers";
import {
  BacklogIcon,
  CalendarIcon,
  ComponentIcon,
  EpicIcon,
  ReleaseIcon,
  SprintIcon,
} from "app/components/Icons";
import StoryIcon from "app/components/Icons/StoryIcon";
import { usePlanningDetail } from "../hooks/usePlanningDetail";

interface Props {
  isActive: boolean;
  viewBy: PlanningViewBy;
  group: IPlanningViewsGroup;
  shownSprintDetails?: boolean;
  onTileClick?: () => void;
}

export default function Tiles({
  isActive = false,
  shownSprintDetails = false,
  viewBy,
  group,
  onTileClick,
}: Props) {
  const [isHovered, setIsHovered] = useState(false);
  const {
    canOpenPlanningDetail,
    canOpenSprintDetail,
    handlePlanningTileNameClick,
  } =
    usePlanningDetail();
  const { cardRef, isDraggedOver, isPlanningDropTarget } = usePlanningSprintDrop(
    viewBy,
    group,
  );

  const {
    allocatedCapacity,
    color,
    endDate,
    isUnassigned,
    name,
    startDate,
    ticketTypes,
    totalCapacityHours,
  } = group;

  const isEpicView = viewBy === PlanningViewBy.EPICS;
  const epicColor = isEpicView && color ? color : "";

  const renderIcon = (type?: PlanningViewBy, isCanceled?: boolean) => {
    switch (type) {
      case PlanningViewBy.COMPONENTS:
        return <ComponentIcon isCanceled={isCanceled} size={16} />;
      case PlanningViewBy.BACKLOG:
        return <BacklogIcon size={16} />;
      case PlanningViewBy.EPICS:
        return (
          <EpicIcon
            isCanceled={isCanceled}
            size={16}
            color={epicColor || "#79808A"}
          />
        );
      case PlanningViewBy.SPRINTS:
        return <SprintIcon isCanceled={isCanceled} size={16} />;
      case PlanningViewBy.STORIES:
        return <StoryIcon isCanceled={isCanceled} size={16} />;
      case PlanningViewBy.NEXT_RELEASES:
      case PlanningViewBy.PAST_RELEASES:
        return <ReleaseIcon isCanceled={isCanceled} size={16} />;
      default:
        return null;
    }
  };

  const icon = renderIcon(viewBy, isUnassigned);
  const showNameClickable =
    canOpenPlanningDetail(viewBy, group) || canOpenSprintDetail(viewBy, group);

  const clonedIcon =
    icon &&
    React.cloneElement(icon as React.ReactElement, {
      color: epicColor || "#5A8DF1",
    });
  return (
    <Card
      ref={cardRef}
      className={clsx(
        "group flex flex-col gap-2 border min-w-0 max-w-full overflow-hidden",
        !isPlanningDropTarget && "cursor-pointer hover:border-secondary",
        isDraggedOver && isPlanningDropTarget && "!border-primary bg-primary50",
        !isDraggedOver && (isActive ? "border-primary" : "border-gray-100"),
        !isDraggedOver && isPlanningDropTarget && "hover:border-secondary",
      )}
      onClick={onTileClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex gap-1 items-start min-w-0 w-full">
        <span className="flex-shrink-0">{isHovered || isActive ? clonedIcon : icon}</span>
        <span
          title={name}
          onClick={(event) => handlePlanningTileNameClick(viewBy, group, event)}
          className={clsx(
            "text-sm min-w-0 flex-1 truncate",
            showNameClickable && "cursor-pointer hover:underline",
            !epicColor && (isActive ? "text-primary font-medium" : "text-black font-normal"),
            epicColor && "font-normal",
            epicColor && isActive && "font-medium",
          )}
          {...(epicColor ? { style: { color: epicColor } } : {})}
        >
          {name}
        </span>
      </div>
      <div className="flex items-center justify-between gap-1 min-w-0 w-full">
        <span className="text-xs text-secondary shrink-0">Ticket types</span>
        <div className="flex flex-wrap justify-end gap-x-2 gap-y-1 min-w-0">
          {ticketTypes.map((item) => (
            <div
              key={`${item.typeName ?? item.icon}`}
              className="flex items-center gap-1 shrink-0"
            >
              <TicketTypeIcon
                iconName={item.typeName?.toLowerCase() as string}
                size={16}
              />
              <span className="text-xs text-secondary">
                {item.count ?? "-"}
              </span>
            </div>
          ))}
        </div>
      </div>

      {shownSprintDetails && (
        <>
          <div className="flex justify-between items-start gap-2 min-w-0 w-full">
            <span className="text-xs text-secondary shrink-0">Capacity hours</span>
            <span className="text-xs text-secondary text-right break-words min-w-0">
              {allocatedCapacity ?? "-"} / {totalCapacityHours ?? "-"}
            </span>
          </div>
          <div className="flex justify-between items-start gap-2 min-w-0 w-full">
            <span className="text-xs text-secondary shrink-0">Start - End dates</span>
            <span className="text-[10px] text-secondary flex items-center gap-1 text-right break-words min-w-0">
              <span className="flex-shrink-0">
                <CalendarIcon size={12} />
              </span>
              {startDate && endDate
                ? `${dateFormatter(startDate)} / ${dateFormatter(endDate)}`
                : "-"}
            </span>
          </div>
        </>
      )}
    </Card>
  );
}
